import React, { useState } from 'react';
import { Empty } from 'antd';

// 'YYYY-MM-DD' → 'DD-MM' (formato de fecha argentino en el gráfico)
const ddmm = (fecha) => {
  const [, m, d] = String(fecha).split('-');
  return `${d}-${m}`;
};

/**
 * Gráfico de línea simple en SVG.
 *
 * Espera `[{ fecha: 'YYYY-MM-DD', visitas: n }]`, que es lo que devuelven
 * `/analytics/tablero` y `/analytics/catalogo/:id`.
 *
 * VIVE ACA Y NO EN `pages/Analytics/`, donde nació, porque lo consumen DOS
 * dominios: el tablero de un remate y las métricas del catálogo de genética,
 * que está en Herramientas. La regla de AGENTS.md es el ALCANCE y no el
 * tamaño: lo que cruza dominios SE MUDA, no se copia ni se alcanza desde el
 * otro lado. Importarlo desde `pages/Analytics/Analytics` arrastraba la página
 * entera —`eventoService`, `descargarBlob`, su juego de iconos— al bundle de
 * Herramientas para dibujar una curva.
 */
const LineChart = ({ data }) => {
  // Hook antes de cualquier return condicional (regla de hooks).
  const [hover, setHover] = useState(null);

  if (!data || data.length === 0) {
    return <Empty description="Sin datos de visitas" style={{ padding: '40px 0' }} />;
  }

  const W = 600;
  const H = 180;
  const PAD = { top: 16, right: 16, bottom: 32, left: 40 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const maxVal = Math.max(...data.map((d) => d.visitas), 1);
  const minVal = 0;

  const xScale = (i) => PAD.left + (i / Math.max(data.length - 1, 1)) * innerW;
  const yScale = (v) => PAD.top + innerH - ((v - minVal) / (maxVal - minVal)) * innerH;

  const points = data.map((d, i) => `${xScale(i)},${yScale(d.visitas)}`).join(' ');
  const areaPoints = [
    `${xScale(0)},${PAD.top + innerH}`,
    ...data.map((d, i) => `${xScale(i)},${yScale(d.visitas)}`),
    `${xScale(data.length - 1)},${PAD.top + innerH}`,
  ].join(' ');

  // Etiquetas del eje X — mostrar solo ~6 fechas
  const step = Math.max(1, Math.floor(data.length / 6));
  const xLabels = data.filter((_, i) => i % step === 0 || i === data.length - 1);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {/* Área de relleno */}
      <polygon points={areaPoints} fill="rgba(22, 119, 255, 0.08)" />
      {/* Línea */}
      <polyline
        points={points}
        fill="none"
        stroke="#1677ff"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* Puntos */}
      {data.map((d, i) => (
        <circle
          key={i}
          cx={xScale(i)}
          cy={yScale(d.visitas)}
          r={hover === i ? 5 : 3.5}
          fill="#1677ff"
          stroke="#fff"
          strokeWidth="1.5"
        />
      ))}
      {/* Áreas de hover — círculos invisibles más grandes para capturar el mouse */}
      {data.map((d, i) => (
        <circle
          key={`hit-${i}`}
          cx={xScale(i)}
          cy={yScale(d.visitas)}
          r="12"
          fill="transparent"
          style={{ cursor: 'pointer' }}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
        />
      ))}
      {/* Eje X — etiquetas */}
      {xLabels.map((d, idx) => {
        const i = data.indexOf(d);
        return (
          <text
            key={idx}
            x={xScale(i)}
            y={H - 6}
            textAnchor="middle"
            fontSize="9"
            fill="#8c8c8c"
          >
            {ddmm(d.fecha)} {/* DD-MM */}
          </text>
        );
      })}
      {/* Eje Y — valor máximo */}
      <text x={PAD.left - 4} y={PAD.top + 4} textAnchor="end" fontSize="9" fill="#8c8c8c">
        {maxVal}
      </text>
      <text x={PAD.left - 4} y={PAD.top + innerH} textAnchor="end" fontSize="9" fill="#8c8c8c">
        0
      </text>
      {/* Tooltip al hacer hover sobre un punto */}
      {hover !== null && (() => {
        const d = data[hover];
        const cx = xScale(hover);
        const cy = yScale(d.visitas);
        const label = `${ddmm(d.fecha)} · ${d.visitas} ${d.visitas === 1 ? 'visita' : 'visitas'}`;
        const boxW = label.length * 5.4 + 12;
        const boxH = 18;
        const bx = Math.max(2, Math.min(cx - boxW / 2, W - boxW - 2));
        const by = cy - boxH - 8 < 2 ? cy + 8 : cy - boxH - 8;
        return (
          <g pointerEvents="none">
            <rect x={bx} y={by} width={boxW} height={boxH} rx="4" fill="#1f1f1f" opacity="0.92" />
            <text x={bx + boxW / 2} y={by + 12.5} textAnchor="middle" fontSize="10" fontWeight="600" fill="#fff">
              {label}
            </text>
          </g>
        );
      })()}
    </svg>
  );
};

export default LineChart;
